import JSZip from "jszip";
import { mmToPt } from "./units";
import { slotsForPage, computeLayout } from "./imposition";

function buildJpgFilename(pageIndex = null) {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");

  const ts =
    `${d.getFullYear()}` +
    `${pad(d.getMonth() + 1)}` +
    `${pad(d.getDate())}_` +
    `${pad(d.getHours())}` +
    `${pad(d.getMinutes())}` +
    `${pad(d.getSeconds())}`;

  if (pageIndex !== null) {
    return `KAPEI_Imposer_PRO_p${pageIndex + 1}_${ts}.jpg`;
  }

  return `KAPEI_Imposer_PRO_${ts}.jpg`;
}

async function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

function computeImageRect(frameX, frameY, frameW, frameH, imgW, imgH, adjust) {
  const fit = adjust.fit || "fill";
  const zoom = Math.max(0.05, adjust.zoom || 1);

  const sFit = Math.min(frameW / imgW, frameH / imgH);
  const sFill = Math.max(frameW / imgW, frameH / imgH);

  const sBase = fit === "fit" ? sFit : sFill;

  const sX = adjust.distort
    ? sBase * (adjust.scaleX || 1)
    : sBase * zoom;

  const sY = adjust.distort
    ? sBase * (adjust.scaleY || 1)
    : sBase * zoom;

  const drawW = imgW * sX;
  const drawH = imgH * sY;

  const offsetX = mmToPt(adjust.offset_x || 0) * 4;
  const offsetY = mmToPt(adjust.offset_y || 0) * 4;

  return {
    x: frameX + (frameW - drawW) / 2 + offsetX,
    y: frameY + (frameH - drawH) / 2 + offsetY,
    w: drawW,
    h: drawH,
  };
}

export async function exportProjectToJPGZip(state) {
  const {
    page,
    piece,
    copies,
    separation,
    bleed,
    images,
    slotAdjustments,
    pageOffsets,
  } = state;

  const layout = computeLayout({
    page,
    piece,
    copies,
    separation,
    bleed,
  });

  if (layout.pages === 0) {
    throw new Error("No caben piezas en la hoja.");
  }

  const zip = new JSZip();

  const SCALE = 4;

  const pagePxW = Math.round(mmToPt(page.width_mm) * SCALE);
  const pagePxH = Math.round(mmToPt(page.height_mm) * SCALE);

  const loadedImages = {};

  for (const img of images) {
    loadedImages[img.id] = await loadImage(img.dataUrl);
  }

  for (let pageIndex = 0; pageIndex < layout.pages; pageIndex++) {
    const canvas = document.createElement("canvas");
    canvas.width = pagePxW;
    canvas.height = pagePxH;

    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const { slots } = slotsForPage({
      page,
      piece,
      copies,
      separation,
      bleed,
      pageIndex,
    });

    const pageOffset =
      (pageOffsets && pageOffsets[pageIndex]) || { x: 0, y: 0 };

    for (const slot of slots) {
      const adj = slotAdjustments[slot.index] || {};

      if (!adj.imageId) continue;

      const img = loadedImages[adj.imageId];
      if (!img) continue;

      const bleedX =
        (mmToPt(slot.bleed_x_mm + (pageOffset.x || 0))) * SCALE;

      const bleedY =
        (mmToPt(slot.bleed_y_mm + (pageOffset.y || 0))) * SCALE;

      const bleedW = mmToPt(slot.bleed_w_mm) * SCALE;
      const bleedH = mmToPt(slot.bleed_h_mm) * SCALE;

      ctx.save();

      ctx.beginPath();
      ctx.rect(bleedX, bleedY, bleedW, bleedH);
      ctx.clip();

      const rect = computeImageRect(
        bleedX,
        bleedY,
        bleedW,
        bleedH,
        img.width,
        img.height,
        adj
      );

      ctx.translate(rect.x + rect.w / 2, rect.y + rect.h / 2);

      if (adj.rotation) {
        ctx.rotate((adj.rotation * Math.PI) / 180);
      }

      ctx.drawImage(
        img,
        -rect.w / 2,
        -rect.h / 2,
        rect.w,
        rect.h
      );

      ctx.restore();
    }

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.95)
    );

    zip.file(buildJpgFilename(pageIndex), blob);
  }

  const zipBlob = await zip.generateAsync({ type: "blob" });

  const url = URL.createObjectURL(zipBlob);

  const a = document.createElement("a");
  a.href = url;
  a.download = "KAPEI_Imposer_PRO_JPG.zip";

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
